import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Search, Sparkles, Bug, Droplet, Sun, Beaker, Heart, Lock, ChevronRight, AlertTriangle,
} from "lucide-react";

const CATEGORY_META = {
  all: { label: "All", icon: Sparkles, color: "#4CAF50" },
  fungal: { label: "Fungal", icon: Beaker, color: "#7BC67B" },
  pest: { label: "Pests", icon: Bug, color: "#FF8F00" },
  environmental: { label: "Environmental", icon: Sun, color: "#FFD54F" },
  nutritional: { label: "Nutrition", icon: Droplet, color: "#4FC3F7" },
};

const SEVERITY_COLOR = {
  mild: "#4CAF50",
  moderate: "#FF8F00",
  severe: "#D32F2F",
};

function CategoryChip({ id, active, onClick }) {
  const meta = CATEGORY_META[id] || CATEGORY_META.all;
  const Icon = meta.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`healing-category-${id}`}
      className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all backdrop-blur-md"
      style={{
        background: active ? `${meta.color}33` : "rgba(0,0,0,0.55)",
        border: `1px solid ${active ? meta.color + "99" : "rgba(255,255,255,0.18)"}`,
        color: active ? meta.color : "rgba(255,255,255,0.95)",
        boxShadow: active ? `0 0 14px ${meta.color}44` : "0 2px 8px rgba(0,0,0,0.35)",
      }}
    >
      <Icon size={12} strokeWidth={2.2} />
      {meta.label}
    </button>
  );
}

function DiseaseCard({ disease, locked, onClick }) {
  const sev = disease.severity_typical || "mild";
  const sevColor = SEVERITY_COLOR[sev] || "#4CAF50";
  const meta = CATEGORY_META[disease.category] || CATEGORY_META.all;
  const Icon = meta.icon;

  return (
    <button
      onClick={onClick}
      data-testid={`healing-card-${disease.id}`}
      className="glass-card w-full text-left p-4 fade-up transition-all hover:scale-[1.01] backdrop-blur-xl"
      style={{
        background: "rgba(8,12,18,0.82)",
        border: `1px solid ${meta.color}55`,
        borderRadius: 18,
        boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: `${meta.color}26`,
            border: `1px solid ${meta.color}66`,
          }}
        >
          <Icon size={18} style={{ color: meta.color }} strokeWidth={1.9} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-white truncate">{disease.name}</h3>
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
              style={{ background: `${sevColor}33`, color: "#fff", border: `1px solid ${sevColor}` }}
            >
              {sev}
            </span>
          </div>
          <p className="text-[13px] line-clamp-2 mb-2" style={{ color: "rgba(255,255,255,0.92)" }}>{disease.description}</p>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: meta.color }}>
              {meta.label}
            </span>
            {locked ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-[#FFD54F] font-semibold">
                <Lock size={10} /> Premium
              </span>
            ) : (
              <ChevronRight size={14} className="text-[#4CAF50]" />
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function HealingScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(["all", "fungal", "pest", "environmental", "nutritional"]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (category !== "all") params.set("category", category);
        if (q) params.set("q", q);
        const { data } = await api.get(`/healing/diseases?${params.toString()}`);
        setItems(data.items || []);
        if (data.categories) setCategories(data.categories);
      } catch (err) {
        console.error("[Healing] load failed:", err);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, category]);

  const isFree = user?.tier === "free";
  const counts = useMemo(() => ({ total: items.length }), [items]);

  return (
    <Shell section="remedies">
      <div className="flex items-center gap-2 mb-2 fade-up">
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-full"
          style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}
        >
          <Heart size={18} className="text-[#4CAF50]" strokeWidth={1.6} />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-shadow-green" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.85), 0 0 18px rgba(76,175,80,0.55)" }}>Healing & Remedies</h1>
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "rgba(255,255,255,0.85)", textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}>
            {counts.total} {counts.total === 1 ? "condition" : "conditions"} · curated by botanists
          </p>
        </div>
      </div>

      {isFree && (
        <div
          className="mb-4 p-3 rounded-xl flex items-start gap-2 fade-up stagger-1 backdrop-blur-md"
          style={{
            background: "rgba(28,20,4,0.78)",
            border: "1px solid rgba(255, 213, 79, 0.55)",
            boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
          }}
          data-testid="healing-premium-banner"
        >
          <AlertTriangle size={14} className="text-[#FFD54F] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.95)" }}>
              Browse all conditions freely. Unlock full <strong className="text-[#FFD54F]">5-step remedy plans</strong> with Premium.
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="text-[11px] font-semibold text-[#FFD54F] underline mt-1"
              data-testid="healing-upgrade-btn"
            >
              Upgrade for ${"$"}3.99/mo →
            </button>
          </div>
        </div>
      )}

      <div className="glass-card flex items-center gap-3 px-4 py-3 mb-4 fade-up stagger-2 backdrop-blur-xl"
           style={{ background: "rgba(8,12,18,0.78)", border: "1px solid rgba(76,175,80,0.35)", boxShadow: "0 4px 18px rgba(0,0,0,0.4)" }}>
        <Search size={18} className="text-[#4CAF50]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by symptom or disease name…"
          className="flex-1 bg-transparent outline-none text-white placeholder-white/65 text-sm"
          data-testid="healing-search-input"
        />
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 fade-up stagger-3"
           data-testid="healing-category-chips">
        {categories.map((cid) => (
          <CategoryChip
            key={cid}
            id={cid}
            active={category === cid}
            onClick={() => setCategory(cid)}
          />
        ))}
      </div>

      {loading && items.length === 0 ? (
        <p className="text-center text-soft text-sm py-10">Loading conditions…</p>
      ) : items.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="healing-empty">
          <Heart size={32} className="text-[#4CAF50]/40 mx-auto mb-2" />
          <p className="text-soft text-sm">No matches. Try a different keyword or category.</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="healing-list">
          {items.map((d) => (
            <DiseaseCard
              key={d.id}
              disease={d}
              locked={isFree}
              onClick={() => navigate(`/healing/${d.id}`)}
            />
          ))}
        </div>
      )}
    </Shell>
  );
}
